import React, { useMemo, useEffect, useCallback } from "react";
import { AutocompleteInput, ReferenceInput, required, SelectInput, NumberInput } from "react-admin";
import CustomDateInput from "shared/components/inputs/CustomDateInput";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { OptimizerCertificationStatusEnum, OptimizerFinlandGroupCertificationTypeEnum } from "shared/enums/opti_enums";
import { optimizerCertificationStatusChoices } from "shared/enums/reactAdminChoices";
import { SiteResourcesEnum } from "shared/enums/resourceMappings";
import { useFormContext } from "react-hook-form";
import { useGroupCertificationCreate } from "entities/optimizer/groups_certifications/create/hooks/useGroupCertificationCreate";

interface FormFieldsProps {
  setCertifiedPower: (power: number) => void;
  setMarket: (certifName: OptimizerFinlandGroupCertificationTypeEnum) => void;
}

const FinlandFormFields: React.FC<FormFieldsProps> = ({ setCertifiedPower, setMarket }) => {
  const { isLoading, certificationIdByName, certificationNameById, getCertificationPowerFromId } =
    useGroupCertificationCreate();
  const { setValue, watch } = useFormContext();
  const certificationId = watch("certification_id");

  const certifChoices = useMemo(() => {
    if (certificationIdByName) {
      return Object.entries(certificationIdByName).map(([certifName, id]) => {
        return {
          id,
          name: certifName,
          disabled: !Object.values(OptimizerFinlandGroupCertificationTypeEnum).includes(
            certifName as OptimizerFinlandGroupCertificationTypeEnum,
          ),
        };
      });
    }
  }, [certificationIdByName]);

  useEffect(() => {
    const certifName = certificationNameById[certificationId as number];
    if (certifName) {
      setMarket(certifName);
    }
  }, [certificationId, setMarket, certificationNameById]);

  const handleChange = useCallback(
    (power: number | React.ChangeEvent<HTMLInputElement> | undefined) => {
      if (!power) return;

      const powerValue = typeof power === "number" ? power : Number(power.target.value);

      if (!isNaN(powerValue)) {
        setCertifiedPower(powerValue);
        setValue("certified_power", powerValue);
      }
    },
    [setCertifiedPower, setValue],
  );

  useEffect(() => {
    const certifiedPower = getCertificationPowerFromId(certificationId);
    if (certifiedPower) {
      handleChange(certifiedPower);
    }
  }, [certificationId, handleChange, getCertificationPowerFromId]);

  return (
    <>
      <ReferenceInput source="site_id" reference={SiteResourcesEnum.Sites} filter={{ country: SiteContryEnum.Finland }}>
        <AutocompleteInput source="name" validate={required()} />
      </ReferenceInput>

      <SelectInput isLoading={isLoading} source="certification_id" choices={certifChoices} validate={required()} />

      <NumberInput source="certified_power" label="Certified Power" validate={required()} onChange={handleChange} />

      <CustomDateInput
        source="start_date"
        label="Start Date"
        fullWidth
        validate={required()}
        defaultValue={new Date()}
      />

      <SelectInput
        source="status"
        choices={optimizerCertificationStatusChoices}
        validate={required()}
        defaultValue={OptimizerCertificationStatusEnum.TRIAL}
      />
    </>
  );
};

export default FinlandFormFields;
